import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Package, Shirt, Zap, Scissors, Home } from "lucide-react";
import { API } from "../constants";
import { SEO } from "../components/SEO";

const Products = () => {
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getProductIcon = (productId) => {
    switch(productId) {
      case 'dtf_printing': return <Zap className="w-8 h-8 text-orange-500" />;
      case 'screen_printing': return <Shirt className="w-8 h-8 text-blue-500" />;
      case 'embroidery': return <Scissors className="w-8 h-8 text-purple-500" />;
      case 'fastisol_transfers': return <Home className="w-8 h-8 text-red-500" />;
      default: return <Package className="w-8 h-8 text-gray-500" />;
    }
  };

  const getProductDescription = (productId, originalDescription) => {
    if (productId === 'fastisol_transfers') {
      return "DIY heat transfer printing for home use - no garment needed";
    }
    return originalDescription;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO
        title="Printing Services"
        description="Choose from DTF, screen printing, embroidery, stickers, and more. Professional custom printing with instant pricing and smart artwork analysis."
        path="/products"
      />
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Professional Printing Services</h1>
        <p className="text-xl text-gray-600">Choose your printing method and get instant pricing</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
        {Object.entries(products).map(([id, product]) => (
          <Link to={`/product/${id}`} key={id}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <div className="aspect-video overflow-hidden rounded-t-lg">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform"
                />
              </div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  {getProductIcon(id)}
                </div>
                <CardDescription className="text-sm">{getProductDescription(id, product.description)}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-orange-600">
                    {id === 'stickers' ? 'Die Cut & Kiss Cut' : 'Smart Analysis Included'}
                  </span>
                  <Button size="sm" data-testid={`product-${id}-btn`}>
                    Configure & Quote
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

// Enhanced Product Detail Component with Fixed Location Selection and New Embroidery System

export { Products };
export default Products;
